import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { useAuthStore } from '../../store/useAuthStore';
import { keySession } from '../../services/assistant/keySession';
import { useNovaStore } from '../../store/useNovaStore';
import { clearAllDrafts } from '../../hooks/useChannelDraft';

export function AuthProvider({ children }: { children: ReactNode }) {
  const { setSession, setCollaborator, setClientUser, setInitialized, setIdentityResolved } =
    useAuthStore();

  async function fetchIdentity(authUserId: string) {
    const [collabRes, clientRes] = await Promise.all([
      supabase
        .from('collaborators')
        .select('*')
        .eq('auth_user_id', authUserId)
        .eq('is_active', true)
        .maybeSingle(),
      supabase
        .from('client_users')
        .select('*')
        .eq('auth_user_id', authUserId)
        .eq('is_active', true)
        .maybeSingle(),
    ]);
    setCollaborator(collabRes.data ?? null);
    setClientUser(clientRes.data ?? null);
  }

  useEffect(() => {
    let mounted = true;
    // auth_user_id dont l'identité est déjà résolue. Supabase réémet un event
    // (TOKEN_REFRESHED, et un SIGNED_IN au retour d'onglet) sans que l'utilisateur
    // change : re-fetcher l'identité à ce moment repasse identityResolved à false
    // et fait clignoter le BrandedLoader plein écran. On ne re-résout donc que
    // lorsque l'utilisateur change réellement.
    let resolvedUserId: string | null = null;

    // Source unique de vérité : onAuthStateChange.
    // Supabase émet un event 'INITIAL_SESSION' au mount qui remplace getSession()
    // et résout le problème de promise pending en StrictMode/React 19.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;

      // Sync session + marquer initialisé IMMÉDIATEMENT (débloque le spinner)
      setSession(session);
      setInitialized(true);

      if (session?.user) {
        const userChanged = session.user.id !== resolvedUserId;

        // Fetch de l'identité (collaborator OU client_user) uniquement au premier
        // event pour cet utilisateur — pas à chaque refresh de token.
        if (userChanged) {
          resolvedUserId = session.user.id;
          setIdentityResolved(false);
          fetchIdentity(session.user.id)
            .catch((err) => {
              console.error('[AuthProvider] fetchIdentity failed:', err);
            })
            .finally(() => {
              if (mounted) setIdentityResolved(true);
            });
        }

        // Login réel (pas un simple refresh de token) → enregistrer la connexion.
        if (event === 'SIGNED_IN') {
          supabase.rpc('record_login').then(({ error }) => {
            if (error) console.error('[AuthProvider] record_login failed:', error);
          });
        }
      } else {
        resolvedUserId = null;
        setCollaborator(null);
        setClientUser(null);
        setIdentityResolved(true);
        if (event === 'SIGNED_OUT') {
          keySession.lock();
          useNovaStore.getState().setKeyUnlocked(false);
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <>{children}</>;
}

export function useSignOut() {
  const navigate = useNavigate();
  const clear = useAuthStore((s) => s.clear);

  return async function signOut() {
    // Les brouillons de messages peuvent contenir des éléments couverts par le secret
    // professionnel : rien ne doit survivre à la fin de session sur un poste partagé.
    clearAllDrafts();
    clear();
    await supabase.auth.signOut();
    navigate('/login', { replace: true });
  };
}
